import React, { useEffect, useState } from 'react';
import { adminProjectService } from '../services';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { resolveMediaUrl } from '../services/api';
import { notifyError, notifyInfo, notifySuccess } from '../utils/toast';

const Projects = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formError, setFormError] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        location: '',
        cost: '',
        status: 'pending',
        start_date: '',
        end_date: '',
        is_featured: false,
        featured_image: null,
    });

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const response = await adminProjectService.getAll();
            setProjects(response.data.data || []);
        } catch (error) {
            console.error('Error fetching projects:', error);
            notifyError('Unable to load projects.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'file') {
            setFormData(prev => ({ ...prev, [name]: e.target.files?.[0] || null }));
            return;
        }
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        try {
            if (editingId) {
                await adminProjectService.update(editingId, formData);
                notifySuccess('Project updated successfully.');
            } else {
                await adminProjectService.create(formData);
                notifySuccess('Project created successfully.');
            }
            fetchProjects();
            resetForm();
        } catch (error) {
            setFormError(error.response?.data?.message || 'Unable to save project.');
            console.error('Error saving project:', error);
            notifyError(error.response?.data?.message || 'Unable to save project.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure?')) {
            try {
                await adminProjectService.delete(id);
                fetchProjects();
                notifySuccess('Project deleted successfully.');
            } catch (error) {
                console.error('Error deleting project:', error);
                notifyError('Unable to delete project.');
            }
        }
    };

    const handleEdit = (project) => {
        setFormData({
            title: project.title,
            description: project.description,
            location: project.location,
            cost: project.cost,
            status: project.status,
            start_date: project.start_date,
            end_date: project.end_date,
            is_featured: !!project.is_featured,
            featured_image: null,
        });
        setEditingId(project.id);
        setShowForm(true);
        notifyInfo('Project loaded for editing.');
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            location: '',
            cost: '',
            status: 'pending',
            start_date: '',
            end_date: '',
            is_featured: false,
            featured_image: null,
        });
        setEditingId(null);
        setFormError('');
        setShowForm(false);
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-4xl font-bold text-gray-800">Projects</h1>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition flex items-center gap-2"
                >
                    <FaPlus /> Add Project
                </button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="bg-white p-8 rounded-lg shadow-lg mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6">{editingId ? 'Edit Project' : 'New Project'}</h2>
                    {formError && (
                        <div className="mb-4 rounded-lg bg-red-100 p-3 text-red-700">
                            {formError}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Location</label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Cost (PKR)</label>
                            <input
                                type="number"
                                name="cost"
                                value={formData.cost}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            >
                                <option value="pending">Pending</option>
                                <option value="ongoing">Ongoing</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Start Date</label>
                            <input
                                type="date"
                                name="start_date"
                                value={formData.start_date}
                                onChange={handleInputChange}
                                required
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">End Date</label>
                            <input
                                type="date"
                                name="end_date"
                                value={formData.end_date}
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div>
                            <label className="block font-semibold text-gray-700 mb-2">Featured Image</label>
                            <input
                                type="file"
                                name="featured_image"
                                accept="image/*"
                                onChange={handleInputChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <input
                                id="is_featured"
                                type="checkbox"
                                name="is_featured"
                                checked={formData.is_featured}
                                onChange={handleInputChange}
                                className="h-4 w-4"
                            />
                            <label htmlFor="is_featured" className="font-semibold text-gray-700">Mark as featured</label>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block font-semibold text-gray-700 mb-2">Description</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                rows="4"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500"
                            ></textarea>
                        </div>
                        <div className="md:col-span-2 flex gap-4">
                            <button
                                type="submit"
                                className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 transition"
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={resetForm}
                                className="bg-gray-400 text-white px-6 py-2 rounded-lg hover:bg-gray-500 transition"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Projects Table */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-emerald-600 text-white">
                                <tr>
                                    <th className="px-6 py-3 text-left">Title</th>
                                    <th className="px-6 py-3 text-left">Location</th>
                                    <th className="px-6 py-3 text-left">Cost</th>
                                    <th className="px-6 py-3 text-left">Image</th>
                                    <th className="px-6 py-3 text-left">Status</th>
                                    <th className="px-6 py-3 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projects.map((project) => (
                                    <tr key={project.id} className="border-b hover:bg-gray-50">
                                        <td className="px-6 py-4">{project.title}</td>
                                        <td className="px-6 py-4">{project.location}</td>
                                        <td className="px-6 py-4">PKR {project.cost?.toLocaleString()}</td>
                                        <td className="px-6 py-4">
                                            {project.featured_image && (
                                                <img
                                                    src={resolveMediaUrl(project.featured_image)}
                                                    alt={project.title}
                                                    className="h-10 w-10 rounded object-cover"
                                                />
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-3 py-1 rounded-full text-white text-xs font-semibold ${project.status === 'completed' ? 'bg-green-500' :
                                                    project.status === 'ongoing' ? 'bg-emerald-500' :
                                                        'bg-yellow-500'
                                                }`}>
                                                {project.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 flex gap-3">
                                            <button
                                                onClick={() => handleEdit(project)}
                                                className="text-emerald-600 hover:text-emerald-800"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(project.id)}
                                                className="text-red-600 hover:text-red-800"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Projects;
